import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiDelete, apiGet, apiPost, apiPut, clearToken, setToken, getToken } from '../api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function login(email, password) {
    const res = await apiPost('/api/auth/login', { email, password });
    setToken(res.token);
    const profile = res.user;
    setCurrentUser({ uid: profile.uid, email: profile.email });
    setUserProfile(profile);
    return profile;
  }

  async function logout() {
    clearToken();
    setCurrentUser(null);
    setUserProfile(null);
  }

  async function getAllUsers() {
    const res = await apiGet('/api/users');
    return res.users || [];
  }

  function getVisibleAgents(allUsers) {
    if (!userProfile) return [];
    if (userProfile.role === 'admin') {
      return allUsers.filter(u => u.role === 'agent');
    }
    if (userProfile.role === 'manager') {
      const assignedIds = userProfile.assignedAgents || [];
      return allUsers.filter(u => assignedIds.includes(u.uid) || assignedIds.includes(u.name));
    }
    return [userProfile]; // agent sees only self
  }

  function getVisibleAgentNames(allUsers) {
    return getVisibleAgents(allUsers).map(a => a.name);
  }

  async function createUser(email, password, profile) {
    const res = await apiPost('/api/users', { email, password, profile });
    return res.uid;
  }

  async function updateUser(uid, data) {
    await apiPut('/api/users/' + uid, data);
  }

  async function deleteUser(uid) {
    await apiDelete('/api/users/' + uid);
  }

  async function resetPassword(email) {
    await apiPost('/api/auth/reset-password', { email });
  }

  async function sendUserCredentialsEmail(payload) {
    await apiPost('/api/users/send-credentials', payload);
  }

  useEffect(() => {
    var mounted = true;
    var bootstrap = async function() {
      if (!getToken()) {
        if (mounted) setLoading(false);
        return;
      }
      try {
        const res = await apiGet('/api/auth/me');
        if (!mounted) return;
        const profile = res.user;
        setCurrentUser({ uid: profile.uid, email: profile.email });
        setUserProfile(profile);
      } catch (_e) {
        clearToken();
        if (!mounted) return;
        setCurrentUser(null);
        setUserProfile(null);
      }
      if (mounted) setLoading(false);
    };
    bootstrap();
    return function() { mounted = false; };
  }, []);

  useEffect(function() {
    var onAuthExpired = function() {
      clearToken();
      setCurrentUser(null);
      setUserProfile(null);
      setLoading(false);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('river-auth-expired', onAuthExpired);
    }
    return function() {
      if (typeof window !== 'undefined') {
        window.removeEventListener('river-auth-expired', onAuthExpired);
      }
    };
  }, []);

  const value = {
    currentUser,
    userProfile,
    login,
    logout,
    getAllUsers,
    getVisibleAgents,
    getVisibleAgentNames,
    createUser,
    updateUser,
    deleteUser,
    resetPassword,
    sendUserCredentialsEmail,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
